import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { get } from "lodash";
import * as moment from "moment";
import { Observable, of } from "rxjs";
import { DynamicActionTemplateMessages } from "src/app/shared/components/dynamic-action-template/dynamic-action-template-messages";
import { DynamicActionTemplateComponent } from "src/app/shared/components/dynamic-action-template/dynamic-action-template.component";
import { DynamicFormComponent } from "src/app/shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "src/app/shared/components/dynamic-form/question-base";
import { DateQuestion } from "src/app/shared/components/dynamic-form/question-date";
import { NotificationService } from "src/app/shared/components/notification/notification.service";
import {
  SimpleDialogComponent,
  SimpleDialogProperties,
} from "src/app/shared/components/simple-dialog/simple-dialog.component";
import { allowDeleteEntity, allowUpdateEntity } from "src/app/shared/utils/helpers/permission.helper";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { QueryParams } from "../../../core/http/query-params.type";
import { LoadingService } from "../../../core/loader/loading.service";
import { DataTableConfig } from "../../../shared/components/data-table/data-table-service.type";
import { DataTableComponent } from "../../../shared/components/data-table/data-table.component";
import { DataTableService } from "../../../shared/components/data-table/data-table.service";
import {
  DataTable,
  DataTableColumn,
  GetDataEvent,
  UpdateDataEvent,
} from "../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../shared/components/delete-modal/delete-modal.component";
import { GenericModalComponent } from "../../../shared/components/generic-modal/generic-modal.component";
import { GenericModalData } from "../../../shared/components/generic-modal/generic-modal.type";
import { QuickViewModalComponent } from "../../../shared/components/quick-view-modal/quick-view-modal.component";
import { yesNoOptions } from "../../../shared/constants/components";
import { FORMAT } from "../../../shared/constants/formats";
import { pattern } from "../../../shared/constants/patterns";
import { VALUE } from "../../../shared/constants/values";
import { BoolToStringPipe } from "../../../shared/pipes/bool-to-string/bool-to-string.pipe";
import { MomentPipe } from "../../../shared/pipes/moment/moment.pipe";
import { MoneyPipe } from "../../../shared/pipes/money/money.pipe";
import { UserInfo } from "../../../shared/types/user-info";
import { removePropsWithKeys } from "../../../shared/utils/objects/object";
import { isBetweenValidator } from "../../../shared/validators/is-between.validator";
import { CompanyService } from "../companies/company.service";
import { ConfigurableOptionService } from "../configurable-options/configurable-options.service";
import { ConfigurableOptionTypes } from "../configurable-options/configurable-options.type";
import { ContractService } from "../contracts/contract.service";
import { ContractStatus } from "../contracts/contract.type";
import { InternalProviderService } from "../internal-provider/internal-provider.service";
import { LocationService } from "../locations/location.service";
import { PartnerService } from "../partners/partner.service";
import { PartnerSpecialTypes } from "../partners/partner.type";
import {
  getAssetCategories,
  getAssetCoverageStatuses,
  getAssetQuotedTerms,
  getAssetSearchFilterOptions,
  getContractAssetSearchFilterOptions,
} from "./asset";
import { AssetService } from "./asset.service";
import { AssetServiceUI } from "./asset.service.ui";
import { Asset, AssetCoverageStatuses, ContractAsset } from "./asset.type";
import { isAsset } from "./assets.shared";

@Injectable({
  providedIn: "root",
})
export class AssetTableService {
  currentUser: UserInfo;
  dialogRef: MatDialogRef<GenericModalComponent>;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private loader: LoadingService,
    private assetService: AssetService,
    private authenticationService: AuthenticationService,
    private partnerService: PartnerService,
    private companyService: CompanyService,
    private contractService: ContractService,
    private locationService: LocationService,
    private configurableOptionService: ConfigurableOptionService,
    private dataTableService: DataTableService<Asset>,
    private assetServiceUI: AssetServiceUI,
    private internalProviderService: InternalProviderService,
    private notificationService: NotificationService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  getAssetsTableConfig = (config?: DataTableConfig<Asset>): DataTable<Asset> => {
    const {
      id,
      initialSearch,
      hidden,
      shown,
      bodyHeight,
      columns,
      actionColumn,
      inlineEdit,
      selection,
      entities,
      syncWithQueryParams,
      clientSideHandling,
      showNotificationAfterUpdate,
      toolTip,
      toolBar,
    } = config || {};

    const isAssetsView = window.location.pathname === "/assets";

    const isRelutech =
      entities?.company?.partner?.specialType === PartnerSpecialTypes.Relutech ||
      this.currentUser.partner?.specialType === PartnerSpecialTypes.Relutech ||
      this.currentUser.company?.partner?.specialType === PartnerSpecialTypes.Relutech;

    const _columns: DataTableColumn<Asset>[] = (
      [
        {
          prop: "info",
          name: "Info",
          width: 65,
          hidden: true,
          draggable: false,
          resizeable: false,
          disabledToggle: true,
          sortable: false,
          frozenLeft: true,
        },
        {
          prop: "action",
          name: "Action",
          width: 100,
          hidden: true,
          draggable: false,
          resizeable: false,
          disabledToggle: true,
          frozenLeft: true,
        },
        {
          prop: "ticket",
          name: "Create Ticket",
          width: 100,
          button: {
            icon: "post_add",
            disabled: (asset) => !asset.contract || asset.coverageStatus !== AssetCoverageStatuses.active,
            onClick: this.addTicket,
            tooltip: "Create Ticket",
          },
          draggable: false,
          resizeable: false,
          disabledToggle: true,
          sortable: false,
          exportCsv: {
            skipProperty: true,
          },
          frozenLeft: true,
        },
        {
          prop: "name",
          name: "Name",
          inlineEdit: {
            control: "input",
            validators: () => [Validators.minLength(2), Validators.maxLength(200), Validators.pattern(pattern.words)],
            disabled: (asset) =>
              inlineEdit?.disabled?.(asset) || (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("name")),
          },
        },
        {
          prop: "serialNumber",
          name: "Serial #",
          link: {
            path: (asset) => `/assets/${asset.id}`,
          },
          inlineEdit: {
            control: "input",
            validators: () => [Validators.minLength(1), Validators.maxLength(255), Validators.pattern(pattern.words)],
            disabled: (asset) =>
              !this.currentUser.isGlobalAdmin() ||
              inlineEdit?.disabled?.(asset) ||
              (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("serialNumber")),
          },
        },
        {
          prop: "description",
          name: "Description",
          inlineEdit: {
            control: "input",
            validators: () => [Validators.minLength(1), Validators.maxLength(255)],
            disabled: (asset) =>
              inlineEdit?.disabled?.(asset) || (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("description")),
          },
        },
        {
          prop: "sla.value",
          name: "SLA",
          inlineEdit: {
            control: "search",
            required: false,
            search: {
              columnToUpdate: "slaId",
              propToUpdate: "sla",
              propToDisplay: "value",
              fetchDefaultData: true,
              showNullOption: true,
              service: (formValue: string) =>
                this.configurableOptionService.getConfigurableOptions({
                  where: {
                    ...(formValue && { value: { $cont: formValue } }),
                    type: ConfigurableOptionTypes.Sla,
                  },
                }),
            },
            disabled: () => true,
          },
        },
        {
          prop: "category",
          name: "Category",
          render: (asset) => getAssetCategories().find((c) => c.value === asset.category)?.label,
          inlineEdit: {
            control: "select",
            select: {
              options: [...getAssetCategories()],
            },
            disabled: (asset) =>
              inlineEdit?.disabled?.(asset) || (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("category")),
          },
        },
        {
          prop: "type.model.oem.name",
          name: "OEM",
        },
        {
          prop: "type.model.name",
          name: "Model #",
        },
        {
          prop: "type.name",
          name: "Type",
        },
        {
          prop: "dmr",
          name: "DMR",
          pipes: [{ clazz: BoolToStringPipe }],
          inlineEdit: {
            control: "select",
            select: {
              options: [...yesNoOptions],
            },
            disabled: () => true,
          },
        },
        {
          prop: "monitoring",
          name: "Monitoring",
          pipes: [{ clazz: BoolToStringPipe }],
          inlineEdit: {
            control: "select",
            select: {
              options: [...yesNoOptions],
            },
            disabled: () => true,
          },
        },
        {
          prop: "startDate",
          name: "Start Date",
          inlineEdit: {
            control: "date",
            validators: (asset) => [
              isBetweenValidator(
                asset.contract?.startDate || VALUE.DEFAULT_MIN_DATE,
                asset.contract?.endDate || VALUE.DEFAULT_MAX_DATE,
                "[]",
              ),
            ],
            date: {
              min: (asset) => moment(asset.contract?.startDate || VALUE.DEFAULT_MIN_DATE).toDate(),
              max: (asset) => moment(asset.contract?.endDate || VALUE.DEFAULT_MAX_DATE).toDate(),
            },
            disabled: () => true,
          },
          pipes: [{ clazz: MomentPipe, args: [FORMAT.DATE] }],
        },
        {
          prop: "endDate",
          name: "End Date",
          inlineEdit: {
            control: "date",
            validators: (asset) => [
              isBetweenValidator(
                asset.contract?.startDate || VALUE.DEFAULT_MIN_DATE,
                asset.contract?.endDate || VALUE.DEFAULT_MAX_DATE,
                "[]",
              ),
            ],
            date: {
              min: (asset) => moment(asset.contract?.startDate || VALUE.DEFAULT_MIN_DATE).toDate(),
              max: (asset) => moment(asset.contract?.endDate || VALUE.DEFAULT_MAX_DATE).toDate(),
            },
            disabled: () => true,
          },
          pipes: [{ clazz: MomentPipe, args: [FORMAT.DATE] }],
        },
        {
          prop: "company.partner.name",
          name: "Partner",
          hidden: !this.currentUser.isGlobal(),
          link: {
            path: (asset) => `/partners/${asset.company.partnerId}`,
          },
        },
        {
          prop: "company.name",
          name: "Company",
          hidden: this.currentUser.isCompanyOrViewAsCompany({ ignoreIfMultiCompanies: true }),
          link: {
            path: (asset) => `/companies/${asset.companyId}`,
            disabled: () => this.currentUser.isCompanyOrViewAsCompany(),
          },
        },
        {
          prop: "contract.name",
          name: "Contract",
          link: {
            path: (asset) => `/contracts/${asset.contractId}`,
          },
          inlineEdit: {
            control: "search",
            search: {
              columnToUpdate: "contractId",
              propToUpdate: "contract",
              fetchDefaultData: true,
              propToDisplay: "name",
              service: (formValue: string, asset: Asset) =>
                this.contractService.getContracts({
                  where: {
                    ...(formValue && { name: { $cont: formValue } }),
                    companyId: asset.companyId,
                  },
                }),
            },
            disabled: () => true,
          },
        },
        {
          prop: "internalProvider.name",
          name: "Internal Provider",
          link: {
            path: (asset) => `/internal-providers/${asset.internalProviderId}`,
          },
          render: (asset) => asset.internalProvider?.name,
          hidden: !this.currentUser.isGlobal(),
          inlineEdit: {
            control: "search",
            required: false,
            search: {
              columnToUpdate: "internalProviderId",
              propToUpdate: "internalProvider",
              propToDisplay: "name",
              fetchDefaultData: true,
              showNullOption: true,
              service: (formValue: string) =>
                this.internalProviderService.getInternalProviders({
                  where: {
                    ...(formValue && { name: { $cont: formValue } }),
                  },
                }),
            },
            disabled: () => true,
          },
        },
        {
          prop: "coverageStatus",
          name: "Coverage Status",
          render: (asset) => getAssetCoverageStatuses().find((c) => c.value === asset.coverageStatus)?.label,
          disabled: () => true,
        },
        {
          prop: "location.name",
          name: "Location",
          link: {
            path: (asset) => `/locations/${asset.locationId}`,
          },
          inlineEdit: {
            control: "search",
            search: {
              columnToUpdate: "locationId",
              propToUpdate: "location",
              fetchDefaultData: true,
              propToDisplay: "name",
              service: (formValue: string, asset: Asset) =>
                this.locationService.getLocations({
                  where: {
                    ...(formValue && { name: { $cont: formValue } }),
                    companyId: asset.companyId,
                  },
                }),
            },
            disabled: (asset) =>
              inlineEdit?.disabled?.(asset) ||
              (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("location.name")),
          },
        },
        {
          prop: "quotedSla",
          name: "Quoted SLA",
          hidden: !isRelutech && !this.currentUser.isGlobal(),
          inlineEdit: {
            control: "input",
            disabled: (asset) =>
              (!isRelutech && this.currentUser.isPartner()) ||
              this.currentUser.isCompany() ||
              inlineEdit?.disabled?.(asset) ||
              (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("quotedSla")),
            required: false,
            validators: () => [Validators.minLength(0), Validators.maxLength(100)],
          },
        },
        {
          prop: "quotedPrice",
          name: "Quoted Price",
          render: (asset) =>
            asset.quotedPrice === undefined || asset.quotedPrice === null ? undefined : `$ ${asset.quotedPrice}`,
          hidden: !isRelutech && !this.currentUser.isGlobal(),
          inlineEdit: {
            control: "input",
            disabled: (asset) =>
              (!isRelutech && this.currentUser.isPartner()) ||
              this.currentUser.isCompany() ||
              inlineEdit?.disabled?.(asset) ||
              (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("quotedPrice")),
            required: false,
            validators: () => [Validators.pattern(pattern.price)],
          },
        },
        {
          prop: "quotedTerm",
          name: "Quoted Term",
          render: (asset) => getAssetQuotedTerms().find((qt) => qt.value === asset.quotedTerm)?.label,
          hidden: !isRelutech && !this.currentUser.isGlobal(),
          inlineEdit: {
            control: "select",
            disabled: (asset) =>
              (!isRelutech && this.currentUser.isPartner()) ||
              this.currentUser.isCompany() ||
              inlineEdit?.disabled?.(asset) ||
              (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("quotedTerm")),
            select: {
              options: [...getAssetQuotedTerms()],
            },
          },
        },
        {
          prop: "previousChannelWorksCost",
          name: "Previous CW Cost",
          hidden: true,
          pipes: [{ clazz: MoneyPipe }],
          disabled: () => true,
        },
        {
          prop: "channelWorksCost",
          name: "CW Cost",
          hidden: !this.currentUser.isGlobal(),
          pipes: [{ clazz: MoneyPipe }],
          inlineEdit: {
            control: "input",
            required: true,
            validators: () => [Validators.pattern(pattern.price)],
            disabled: () => true,
          },
        },
        {
          prop: "previousPartnerPrice",
          name: "Previous Partner Price",
          hidden: true,
          pipes: [{ clazz: MoneyPipe }],
          disabled: () => true,
        },
        {
          prop: "partnerPrice",
          name: "Partner Price",
          hidden: !this.currentUser.isGlobal(),
          pipes: [{ clazz: MoneyPipe }],
          inlineEdit: {
            control: "input",
            required: true,
            validators: () => [Validators.pattern(pattern.price)],
            disabled: () => true,
          },
        },
        {
          prop: "fairMarketValue",
          name: "FMV",
          pipes: [{ clazz: MoneyPipe }],
          inlineEdit: {
            control: "input",
            required: true,
            validators: () => [Validators.pattern(pattern.fmvAndBookValues)],
            disabled: (asset) =>
              inlineEdit?.disabled?.(asset) ||
              !this.currentUser.isGlobalAdmin() ||
              !asset.company.enableAssetCustomFields,
          },
        },
        {
          prop: "bookValue",
          name: "Book Value",
          pipes: [{ clazz: MoneyPipe }],
          inlineEdit: {
            control: "input",
            required: true,
            validators: () => [Validators.pattern(pattern.fmvAndBookValues)],
            disabled: (asset) =>
              inlineEdit?.disabled?.(asset) ||
              !this.currentUser.isGlobalAdmin() ||
              !asset.company.enableAssetCustomFields,
          },
        },
        {
          prop: "buyPrice",
          name: "Buy Price",
          pipes: [{ clazz: MoneyPipe }],
          hidden: (!isAssetsView && !isRelutech) || !(this.currentUser.isGlobal() || isRelutech),
        },
        {
          prop: "isPending",
          name: "is Pending",
          pipes: [{ clazz: BoolToStringPipe }],
          inlineEdit: {
            control: "input",
            required: true,
            disabled: () => true,
          },
        },
        {
          prop: "hasConfigurationNotes",
          name: "Configuration Notes",
          hidden: !this.currentUser.isGlobal(),
          pipes: [{ clazz: BoolToStringPipe }],
          inlineEdit: {
            control: "select",
            select: {
              options: [...yesNoOptions],
            },
          },
        },
      ] as DataTableColumn<Asset>[]
    )
      .filter((col) => (shown ? shown.includes(col.prop) : true) && (hidden ? !hidden.includes(col.prop) : true))
      .map((col) => ({
        ...col,
        name: columns?.[col.prop]?.name ?? col.name,
        toolTip: (row, column, value) =>
          columns?.[col.prop]?.toolTip?.(row, column, value) ??
          toolTip?.(row, column, value) ??
          col.toolTip?.(row, column, value),
        link: columns?.[col.prop]?.link ?? col.link,
        icon: columns?.[col.prop]?.icon ?? col.icon,
        hidden: columns?.[col.prop]?.hidden ?? col.hidden,
        render: (row, column, value) =>
          columns?.[col.prop]?.render?.(row, column, value) ??
          col.render?.(row, column, value) ??
          get(row, column.prop),
        summaryFunc: (cells) => columns?.[col.prop]?.summaryFunc?.(cells) ?? col.summaryFunc?.(cells) ?? null,
        inlineEdit: columns?.[col.prop]?.inlineEdit ?? {
          ...col?.inlineEdit,
          /** Use the set condition and the new one passed here */
          disabled: (asset) => col?.inlineEdit?.disabled?.(asset) || !this.canEdit(this.currentUser, asset),
        },
      }));

    return {
      id: id ?? "assets",
      entityName: "Asset",
      bodyHeight,
      initialSearch,
      externalPaging: clientSideHandling === undefined ? true : false,
      externalSorting: clientSideHandling === undefined ? true : false,
      syncWithQueryParams: syncWithQueryParams ?? true,
      showNotificationAfterUpdate,
      toolBar: toolBar || {
        exportCsv: {
          filename: "assets",
          data: {
            browser: (queryParams) =>
              this.assetService.getAssets({
                ...queryParams,
                // Select > ContractId is required to populate CW Cost and Partner Price
                // due to the backend function "deletePricesFromAsset" that deletes these values
                select: {
                  ...queryParams.select,
                  contractId: true,
                },
                where: {
                  // In case this method is called from a view like manage-contract
                  // we have to filter the assets by the contract id
                  contractId: entities?.contract?.id,
                  companyId: entities?.company?.id,
                  locationId: entities?.location?.id,
                  ...removePropsWithKeys<Asset>(queryParams.where, ["partner.name", "company.name"]),
                },
              }),
            email: (queryParams) =>
              this.assetService.getAssetsQueue({
                ...queryParams,
                where: {
                  // In case this method is called from a view like manage-contract
                  // we have to filter the assets by the contract id
                  contractId: entities?.contract?.id,
                  companyId: entities?.company?.id,
                  locationId: entities?.location?.id,
                  ...removePropsWithKeys<Asset>(queryParams.where, ["partner.name", "company.name"]),
                },
              }),
          },
        },
      },
      columns: _columns,
      defaultColumns: [],
      selection: {
        ...selection,
        selectedRows: [],
      },
      searchBar: [
        {
          availableSearchFields: getAssetSearchFilterOptions(this.currentUser, {
            companyService: this.companyService,
            partnerService: this.partnerService,
            configurableOptionService: this.configurableOptionService,
            hidden,
            shown,
          }),
        },
      ],
      actionColumn: actionColumn?.hidden
        ? null
        : [
            {
              icon: "zoom_in",
              text: "Quick View",
              function: (asset) => this.viewAsset(asset, config),
              hidden: actionColumn?.quickView?.hidden,
            },
            {
              icon: "visibility",
              text: "View",
              function: this.manageAsset,
              hidden: () => actionColumn?.view?.hidden,
            },
            {
              icon: "clear",
              text: "Delete",
              function: this.deleteAsset,
              hidden: (asset) => !this.canDelete(asset) || actionColumn?.delete?.hidden,
            },
            {
              icon: "error",
              text: "Decommission",
              function: this.decommissionAsset,
              hidden: (asset) => !this.canDecommission(asset) || actionColumn?.decommission?.hidden,
            },
            {
              icon: "undo",
              text: "Un-Decommission",
              function: this.decommissionAsset,
              hidden: (asset) => !this.canUndecommission(asset) || actionColumn?.undecommission?.hidden,
            },
          ],
    };
  };

  getContractAssetsTableConfig = (config?: DataTableConfig<ContractAsset>): DataTable<ContractAsset> => {
    const {
      id,
      initialSearch,
      hidden,
      shown,
      bodyHeight,
      columns,
      actionColumn,
      inlineEdit,
      selection,
      entities,
      syncWithQueryParams,
      clientSideHandling,
      showNotificationAfterUpdate,
      toolTip,
      toolBar,
    } = config || {};

    const _columns: DataTableColumn<ContractAsset>[] = (
      [
        {
          prop: "ticket",
          name: "Create Ticket",
          width: 100,
          button: {
            icon: "post_add",
            disabled: (contractAsset) =>
              !contractAsset.contract || contractAsset.asset.coverageStatus !== AssetCoverageStatuses.active,
            onClick: this.addTicket,
            tooltip: "Create Ticket",
          },
          draggable: false,
          resizeable: false,
          disabledToggle: true,
          sortable: false,
          exportCsv: {
            skipProperty: true,
          },
          frozenLeft: true,
        },
        {
          prop: "asset.name",
          name: "Name",
          inlineEdit: {
            control: "input",
            validators: () => [Validators.minLength(2), Validators.maxLength(200), Validators.pattern(pattern.words)],
            disabled: (contractAsset) =>
              true ||
              inlineEdit?.disabled?.(contractAsset) ||
              (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("name")),
          },
        },
        {
          prop: "asset.serialNumber",
          name: "Serial #",
          link: {
            path: (contractAsset) => `/assets/${contractAsset.assetId}`,
          },
          inlineEdit: {
            control: "input",
            validators: () => [Validators.minLength(1), Validators.maxLength(255), Validators.pattern(pattern.words)],
            disabled: (contractAsset) =>
              true ||
              !this.currentUser.isGlobalAdmin() ||
              inlineEdit?.disabled?.(contractAsset) ||
              (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("serialNumber")),
          },
        },
        {
          prop: "asset.description",
          name: "Description",
          inlineEdit: {
            control: "input",
            validators: () => [Validators.minLength(1), Validators.maxLength(255)],
            disabled: (contractAsset) =>
              true ||
              inlineEdit?.disabled?.(contractAsset) ||
              (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("description")),
          },
        },
        {
          prop: "sla.value",
          name: "SLA",
          inlineEdit: {
            control: "search",
            required: false,
            search: {
              columnToUpdate: "slaId",
              propToUpdate: "sla",
              propToDisplay: "value",
              fetchDefaultData: true,
              showNullOption: true,
              service: (formValue: string) =>
                this.configurableOptionService.getConfigurableOptions({
                  where: {
                    ...(formValue && { value: { $cont: formValue } }),
                    type: ConfigurableOptionTypes.Sla,
                  },
                }),
            },
            disabled: (contractAsset) =>
              inlineEdit?.disabled?.(contractAsset) ||
              (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("sla.value")),
          },
        },
        {
          prop: "asset.category",
          name: "Category",
          render: (contractAsset) => getAssetCategories().find((c) => c.value === contractAsset.asset.category)?.label,
          inlineEdit: {
            control: "select",
            select: {
              options: [...getAssetCategories()],
            },
            disabled: (contractAsset) =>
              true ||
              inlineEdit?.disabled?.(contractAsset) ||
              (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("category")),
          },
        },
        {
          prop: "asset.type.model.oem.name",
          name: "OEM",
        },
        {
          prop: "asset.type.model.name",
          name: "Model #",
        },
        {
          prop: "asset.type.name",
          name: "Type",
        },
        {
          prop: "dmr",
          name: "DMR",
          pipes: [{ clazz: BoolToStringPipe }],
          inlineEdit: {
            control: "select",
            select: {
              options: [...yesNoOptions],
            },
            disabled: (contractAsset) =>
              inlineEdit?.disabled?.(contractAsset) || (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("dmr")),
          },
        },
        {
          prop: "monitoring",
          name: "Monitoring",
          pipes: [{ clazz: BoolToStringPipe }],
          inlineEdit: {
            control: "select",
            select: {
              options: [...yesNoOptions],
            },
            disabled: (contractAsset) =>
              inlineEdit?.disabled?.(contractAsset) ||
              (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("monitoring")),
          },
        },
        {
          prop: "startDate",
          name: "Start Date",
          inlineEdit: {
            control: "date",
            validators: (contractAsset) => [
              isBetweenValidator(
                contractAsset.contract?.startDate || VALUE.DEFAULT_MIN_DATE,
                contractAsset.contract?.endDate || VALUE.DEFAULT_MAX_DATE,
                "[]",
              ),
            ],
            date: {
              min: (contractAsset) => moment(contractAsset.contract?.startDate || VALUE.DEFAULT_MIN_DATE).toDate(),
              max: (contractAsset) => moment(contractAsset.contract?.endDate || VALUE.DEFAULT_MAX_DATE).toDate(),
            },
            disabled: (contractAsset) =>
              inlineEdit?.disabled?.(contractAsset) ||
              (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("startDate")),
          },
          pipes: [{ clazz: MomentPipe, args: [FORMAT.DATE] }],
        },
        {
          prop: "endDate",
          name: "End Date",
          inlineEdit: {
            control: "date",
            validators: (contractAsset) => [
              isBetweenValidator(
                contractAsset.contract?.startDate || VALUE.DEFAULT_MIN_DATE,
                contractAsset.contract?.endDate || VALUE.DEFAULT_MAX_DATE,
                "[]",
              ),
            ],
            date: {
              min: (contractAsset) => moment(contractAsset.contract?.startDate || VALUE.DEFAULT_MIN_DATE).toDate(),
              max: (contractAsset) => moment(contractAsset.contract?.endDate || VALUE.DEFAULT_MAX_DATE).toDate(),
            },
            disabled: (contractAsset) =>
              inlineEdit?.disabled?.(contractAsset) ||
              (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("endDate")),
          },
          pipes: [{ clazz: MomentPipe, args: [FORMAT.DATE] }],
        },
        {
          prop: "internalProvider.name",
          name: "Internal Provider",
          link: {
            path: (contractAsset) => `/internal-providers/${contractAsset.internalProviderId}`,
          },
          render: (contractAsset) => contractAsset.internalProvider?.name,
          hidden: !this.currentUser.isGlobal(),
          inlineEdit: {
            control: "search",
            required: false,
            search: {
              columnToUpdate: "internalProviderId",
              propToUpdate: "internalProvider",
              propToDisplay: "name",
              fetchDefaultData: true,
              showNullOption: true,
              service: (formValue: string) =>
                this.internalProviderService.getInternalProviders({
                  where: {
                    ...(formValue && { name: { $cont: formValue } }),
                  },
                }),
            },
            disabled: (contractAsset) =>
              inlineEdit?.disabled?.(contractAsset) ||
              (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("internalProvider.name")),
          },
        },
        {
          prop: "coverageStatus",
          name: "Coverage Status",
          render: (contractAsset) =>
            getAssetCoverageStatuses().find((c) => c.value === contractAsset.coverageStatus)?.label,
        },
        {
          prop: "asset.location.name",
          name: "Location",
          link: {
            path: (contractAsset) => `/locations/${contractAsset.asset.locationId}`,
          },
          inlineEdit: {
            control: "search",
            search: {
              columnToUpdate: "locationId",
              propToUpdate: "location",
              fetchDefaultData: true,
              propToDisplay: "name",
              service: (formValue: string, contractAsset: ContractAsset) =>
                this.locationService.getLocations({
                  where: {
                    ...(formValue && { name: { $cont: formValue } }),
                    companyId: contractAsset.asset.companyId,
                  },
                }),
            },
            disabled: (contractAsset) =>
              true ||
              inlineEdit?.disabled?.(contractAsset) ||
              (inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("location.name")),
          },
        },
        {
          prop: "channelWorksCost",
          name: "CW Cost",
          hidden: !this.currentUser.isGlobal(),
          pipes: [{ clazz: MoneyPipe }],
          inlineEdit: {
            control: "input",
            required: true,
            validators: () => [Validators.pattern(pattern.price)],
            disabled: () => inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("channelWorksCost"),
          },
        },
        {
          prop: "partnerPrice",
          name: "Partner Price",
          hidden: !this.currentUser.isGlobal(),
          pipes: [{ clazz: MoneyPipe }],
          inlineEdit: {
            control: "input",
            required: true,
            validators: () => [Validators.pattern(pattern.price)],
            disabled: () => inlineEdit?.editOnly && !inlineEdit?.editOnly?.includes("partnerPrice"),
          },
        },
        {
          prop: "asset.fairMarketValue",
          name: "FMV",
          hidden: !entities.contract.company.enableAssetCustomFields,
          pipes: [{ clazz: MoneyPipe }],
          render: (contractAsset) => contractAsset.asset.fairMarketValue,
        },
        {
          prop: "asset.bookValue",
          name: "Book Value",
          hidden: !entities.contract.company.enableAssetCustomFields,
          pipes: [{ clazz: MoneyPipe }],
          render: (contractAsset) => contractAsset.asset.bookValue,
        },
        {
          prop: "contract.number",
          name: "Contract #",
          render: (contractAsset) => contractAsset.contract?.numberVersion,
          exportCsv: {
            browserColumnKey: "contract.numberVersion",
            csvMapperKey: "contract.numberVersion",
          },
        },
        {
          prop: "asset.company.companyIdentifier",
          name: "Company Identifier",
          render: (contractAsset) => contractAsset.asset?.company?.companyIdentifier,
          exportCsv: {
            browserColumnKey: "asset.company.companyIdentifier",
            csvMapperKey: "asset.company.companyIdentifier",
          },
        },
      ] as DataTableColumn<ContractAsset>[]
    )
      .filter((col) => (shown ? shown.includes(col.prop) : true) && (hidden ? !hidden.includes(col.prop) : true))
      .map((col) => ({
        ...col,
        name: columns?.[col.prop]?.name ?? col.name,
        toolTip: (row, column, value) =>
          columns?.[col.prop]?.toolTip?.(row, column, value) ??
          toolTip?.(row, column, value) ??
          col.toolTip?.(row, column, value),
        link: columns?.[col.prop]?.link ?? col.link,
        icon: columns?.[col.prop]?.icon ?? col.icon,
        hidden: columns?.[col.prop]?.hidden ?? col.hidden,
        render: (row, column, value) =>
          columns?.[col.prop]?.render?.(row, column, value) ??
          col.render?.(row, column, value) ??
          get(row, column.prop),
        summaryFunc: (cells) => columns?.[col.prop]?.summaryFunc?.(cells) ?? col.summaryFunc?.(cells) ?? null,
        inlineEdit: columns?.[col.prop]?.inlineEdit ?? {
          ...col?.inlineEdit,
          /** Use the set condition and the new one passed here */
          disabled: (contractAsset) =>
            col?.inlineEdit?.disabled?.(contractAsset) || !this.canEdit(this.currentUser, contractAsset),
        },
      }));

    return {
      id: id ?? "assets",
      entityName: "Asset",
      bodyHeight,
      initialSearch,
      externalPaging: clientSideHandling === undefined ? true : false,
      externalSorting: clientSideHandling === undefined ? true : false,
      syncWithQueryParams: syncWithQueryParams ?? true,
      showNotificationAfterUpdate,
      toolBar: toolBar || {
        exportCsv: {
          filename: "assets",
          data: {
            browser: (queryParams) =>
              this.assetService.getContractAssets({
                ...queryParams,
                where: {
                  contractId: entities?.contract?.id,
                  ...removePropsWithKeys<ContractAsset>(queryParams.where, ["partner.name", "company.name"]),
                },
                select: {
                  ...queryParams.select,
                  contract: { ...queryParams.select.contract, number: true, version: true },
                },
              }),
            email: (queryParams) =>
              this.assetService.getContractAssetsQueue({
                ...queryParams,
                where: {
                  contractId: entities?.contract?.id,
                  ...removePropsWithKeys<ContractAsset>(queryParams.where, ["partner.name", "company.name"]),
                },
                select: {
                  ...queryParams.select,
                  contract: { ...queryParams.select.contract, number: true, version: true },
                },
              }),
          },
        },
      },
      columns: _columns,
      defaultColumns: [],
      selection: {
        ...selection,
        selectedRows: [],
      },
      searchBar: [
        {
          availableSearchFields: getContractAssetSearchFilterOptions(this.currentUser, {
            companyService: this.companyService,
            partnerService: this.partnerService,
            configurableOptionService: this.configurableOptionService,
            hidden,
            shown,
          }),
        },
      ],
      actionColumn: actionColumn?.hidden
        ? null
        : [
            {
              icon: "zoom_in",
              text: "Quick View",
              function: (contractAsset) => this.viewAsset(contractAsset, config),
              hidden: actionColumn?.quickView?.hidden,
            },
            {
              icon: "visibility",
              text: "View",
              function: (contractAsset) => this.manageAsset(contractAsset.asset),
              hidden: () => actionColumn?.view?.hidden,
            },
            {
              icon: "clear",
              text: "Delete",
              function: (contractAsset, table) => this.deleteAsset(contractAsset.asset, table),
              hidden: (contractAsset) => !this.canDelete(contractAsset) || actionColumn?.delete?.hidden,
            },
            {
              icon: "cancel",
              text: "Terminate Asset",
              function: (contractAsset, table) => this.terminateContractAsset(contractAsset, table),
              hidden: (contractAsset) => actionColumn?.terminate?.hidden || !this.canTerminate(contractAsset),
            },
            {
              icon: "cancel",
              text: "Unterminate Asset",
              function: (contractAsset, table) => this.unterminateContractAsset(contractAsset, table),
              hidden: (contractAsset) => actionColumn?.unterminate?.hidden || !this.canUnterminate(contractAsset),
            },
          ],
    };
  };

  //#region Table inputs
  getAssets = (event: GetDataEvent<Asset>): void => {
    const { onError, onResult } = event;
    const queryParams: QueryParams<Asset> = {
      ...event.queryParams,
      relations: {
        location: true,
        internalProvider: true,
        sla: true,
        contract: { maintenanceProvider: true },
        company: { partner: true },
      },
      where: {
        ...removePropsWithKeys<Asset>(event?.queryParams?.where, ["partner.name", "company.name"]),
      },
    };
    this.assetService.getAssets(queryParams).subscribe(
      (result) => {
        onResult(result);
      },
      (error) => {
        onError(error);
      },
    );
  };

  getAvailableAssetsToLinkToContract = (contractId: string, event: GetDataEvent<Asset>): void => {
    const { onError, onResult } = event;
    const queryParams = {
      ...event.queryParams,
      relations: {
        location: true,
        internalProvider: true,
        sla: true,
        contract: { maintenanceProvider: true },
        company: { partner: true },
      },
      where: {
        ...removePropsWithKeys<Asset>(event?.queryParams?.where, ["partner.name", "company.name"]),
      },
    };
    this.contractService.getAvailableAssetsToLink(contractId, queryParams).subscribe(
      (result) => {
        onResult(result);
      },
      (error) => {
        onError(error);
      },
    );
  };

  getAssetsPost = (event: GetDataEvent<Asset>): void => {
    const { onError, onResult } = event;
    const queryParams = {
      ...event.queryParams,
      relations: {
        location: true,
        internalProvider: true,
        sla: true,
        contract: { maintenanceProvider: true },
        company: { partner: true },
      },
      where: {
        ...removePropsWithKeys<Asset>(event?.queryParams?.where, ["partner.name", "company.name"]),
      },
    };
    this.assetService.getAssetsPost(queryParams).subscribe(
      (result) => {
        onResult(result);
      },
      (error) => {
        onError(error);
      },
    );
  };

  updateAsset = (event: UpdateDataEvent<Asset>): void => {
    const { onError, onResult, row, updatedValues } = event;
    if (row.id && updatedValues) {
      this.loader.show();
      this.assetService.updateAsset(row.id, { ...updatedValues }).subscribe(
        (data): void => {
          onResult(data);
          this.loader.hide();
        },
        (error): void => {
          onError(error);
          this.loader.hide();
        },
      );
    } else {
      onError();
    }
  };

  updateContractAsset = (event: UpdateDataEvent<ContractAsset>): void => {
    const { onError, onResult, row, updatedValues } = event;
    if (row.id && updatedValues) {
      this.loader.show();
      // If startDate is updated then we have to pass its endDate too
      // else if endDate is updated then we have to pass its startDate too
      if (updatedValues.startDate) {
        updatedValues.endDate = row.endDate;
      } else if (updatedValues.endDate) {
        updatedValues.startDate = row.startDate;
      }
      this.assetService.updateContractAsset(row.id, { ...updatedValues }).subscribe(
        (data): void => {
          onResult(data);
          this.loader.hide();
        },
        (error): void => {
          onError(error);
          this.loader.hide();
        },
      );
    } else {
      onError();
    }
  };
  //#endregion

  /** Check if can edit an asset (on the table) */
  private canEdit = (currentUser: UserInfo, assetOrContractAsset: Asset | ContractAsset): boolean => {
    if (
      currentUser.isGlobal() ||
      (!currentUser.isGlobal() && !assetOrContractAsset.contract?.maintenanceProvider?.isChannelWorks)
    )
      return allowUpdateEntity(assetOrContractAsset, currentUser);
    return false;
  };

  //#region Action methods
  private addTicket = (assetOrContractAsset: Asset | ContractAsset): void => {
    const assetId = isAsset(assetOrContractAsset) ? assetOrContractAsset.id : assetOrContractAsset.assetId;
    if (!assetOrContractAsset.contract?.maintenanceProvider?.isChannelWorks) {
      this.dialogRef = this.assetServiceUI.showTicketCreationWarningModal(assetOrContractAsset);
    } else {
      this.router.navigate(["/tickets/create"], {
        queryParams: { assetId },
      });
    }
  };

  private viewAsset = (assetOrContractAsset: Asset | ContractAsset, config?: DataTableConfig): void => {
    const columns = isAsset(assetOrContractAsset)
      ? this.getAssetsTableConfig(config).columns.filter((col) => col.prop !== "ticket")
      : this.getContractAssetsTableConfig(config).columns.filter((col) => col.prop !== "ticket");

    const asset = isAsset(assetOrContractAsset) ? assetOrContractAsset : assetOrContractAsset.asset;
    const company = asset.company;
    const canManageClientPrice = company.canManageClientPrice;
    const contract = assetOrContractAsset.contract;
    const isChannelWorks = contract?.maintenanceProvider?.isChannelWorks;

    const hidePartnerPrice = (prop: DataTableColumn["prop"]) =>
      !(
        prop === "partnerPrice" &&
        contract &&
        canManageClientPrice &&
        isChannelWorks &&
        !this.currentUser.isCompanyOrViewAsCompany()
      );

    const hideClientPrice = (prop: DataTableColumn["prop"]) =>
      !(prop === "clientPrice" && contract && canManageClientPrice && !isChannelWorks);

    const data = (columns as (DataTableColumn<Asset> | DataTableColumn<ContractAsset>)[])
      .filter((col) => !col.hidden && hidePartnerPrice(col?.prop) && hideClientPrice(col?.prop))
      .map((col) => ({
        name: col.name,
        value: this.dataTableService.parseData(assetOrContractAsset, col, get(assetOrContractAsset, col.prop)),
      }));

    this.dialog.open(QuickViewModalComponent, {
      width: "500px",
      data: {
        title: "Quick-view",
        data,
      },
      panelClass: "quick-view-modal",
    });
  };

  private manageAsset = (asset: Asset): void => {
    this.router.navigate([`/assets/${asset.id}`]);
  };

  private deleteAsset = async (asset: Asset, table: DataTableComponent<Asset>): Promise<void> => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "Asset",
        data: asset,
        url: this.assetService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true, rows: [asset] });
      }
    });
  };

  private canDelete(assetOrContractAsset: Asset | ContractAsset): boolean {
    // Only users with admin rights can delete
    // As a company or partner cannot be deleted if it is associated with a channelwork contract
    if (
      this.currentUser.isGlobal() ||
      (!this.currentUser.isGlobal() && !assetOrContractAsset.contract?.maintenanceProvider?.isChannelWorks)
    )
      return allowDeleteEntity(assetOrContractAsset, this.currentUser);
    return false;
  }

  private canDecommission(asset: Asset): boolean {
    if (
      asset.coverageStatus !== AssetCoverageStatuses.pending &&
      asset.coverageStatus !== AssetCoverageStatuses.active &&
      asset.coverageStatus !== AssetCoverageStatuses.decommissioned &&
      !asset.isPending
    ) {
      return this.canEdit(this.currentUser, asset);
    }
    return false;
  }

  private canUndecommission(asset: Asset): boolean {
    return asset.coverageStatus === AssetCoverageStatuses.decommissioned && this.canEdit(this.currentUser, asset);
  }

  private canTerminate(contractAsset: ContractAsset): boolean {
    return (
      this.canEdit(this.currentUser, contractAsset) && contractAsset.coverageStatus !== AssetCoverageStatuses.terminated
    );
  }

  private canUnterminate(contractAsset: ContractAsset): boolean {
    return (
      this.canEdit(this.currentUser, contractAsset) &&
      contractAsset.coverageStatus == AssetCoverageStatuses.terminated &&
      contractAsset.contract.status !== ContractStatus.terminated
    );
  }

  private decommissionAsset = (asset: Asset, table: DataTableComponent<Asset>): void => {
    const operation = asset.coverageStatus === AssetCoverageStatuses.decommissioned ? "undecommission" : "decommission";
    const title =
      operation === "undecommission"
        ? DynamicActionTemplateMessages.undecommission
        : DynamicActionTemplateMessages.decommission;
    this.dialogRef = this.dialog.open<GenericModalComponent, GenericModalData>(GenericModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        title,
        buttons: { hide: true },
        component: {
          clazz: DynamicActionTemplateComponent,
          inputs: {
            operation,
          },
          events: [
            {
              eventName: "formSubmit",
              handler: () => {
                this.assetService[operation === "undecommission" ? "unDecommissionAsset" : "decommissionAsset"](
                  asset.id,
                ).subscribe(
                  () => {
                    this.dialogRef.close(true);
                    this.notificationService.success(DynamicActionTemplateMessages.assetUpdated);
                    table.search();
                  },
                  (err) => {
                    this.notificationService.error(err);
                  },
                );
              },
            },
          ],
        },
      },
    });
  };

  private terminateContractAsset = (contractAsset: ContractAsset, table: DataTableComponent<Asset>): void => {
    const questions: QuestionBase<string | boolean>[] = [];
    this.generateQuestionsTerminateContractAsset(contractAsset).subscribe((qs) => Object.assign(questions, qs));

    this.dialogRef = this.dialog.open<GenericModalComponent, GenericModalData>(GenericModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        title: DynamicActionTemplateMessages.terminateContractAsset,
        buttons: { hide: true },
        component: {
          clazz: DynamicFormComponent,
          inputs: {
            questions,
          },
          events: [
            {
              eventName: "formSubmit",
              handler: (_dynamicForm: DynamicFormComponent, values: any) => {
                this.assetService.terminateContractAsset(contractAsset.id, values).subscribe(
                  () => {
                    this.dialogRef.close(true);
                    this.notificationService.success(DynamicActionTemplateMessages.assetUpdated);
                    table.search();
                  },
                  (err) => {
                    this.notificationService.error(err);
                  },
                );
              },
            },
          ],
        },
      },
    });
  };

  private unterminateContractAsset = (contractAsset: ContractAsset, table: DataTableComponent<Asset>): void => {
    const dialog = this.dialog.open<SimpleDialogComponent, SimpleDialogProperties>(SimpleDialogComponent, {
      id: "confirmUser",
      panelClass: "custom-dialog-container",
      data: {
        title: DynamicActionTemplateMessages.unterminateContractAsset,
        message: "Do you want to confirm this action?",
        type: "yesNo",
        action: "accept",
      },
    });

    dialog.afterClosed().subscribe(async (result) => {
      if (result) {
        this.assetService.unterminateContractAsset(contractAsset.id).subscribe(
          () => {
            this.dialogRef.close(true);
            this.notificationService.success(DynamicActionTemplateMessages.assetUpdated);
            table.search();
          },
          (err) => {
            this.notificationService.error(err);
          },
        );
      }
    });
  };

  private generateQuestionsTerminateContractAsset(
    contractAsset: ContractAsset,
  ): Observable<QuestionBase<string | boolean>[]> {
    const questions: QuestionBase<string | boolean>[] = [
      new DateQuestion({
        key: "endDate",
        label: "End Date",
        minDate: moment(contractAsset?.startDate).toDate() && moment(contractAsset?.contract?.startDate).toDate(),
        maxDate: moment(contractAsset?.endDate).toDate() && moment(contractAsset?.contract?.endDate).toDate(),
        required: true,
        disabled: false,
        order: 1,
      }),
    ];
    return of(questions.filter((fields) => fields));
  }

  //#endregion
}
